import * as React from "react";
import { History, Link2, MapPin, MousePointer2, Sparkles, Trash2, X } from "lucide-react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import {
  apiErrorMessage,
  createRollingLink,
  draftBookingDescription,
  getEntitlements,
  listRollingAppointments,
  listIntegrations,
  listAvailabilityPatterns,
  previewRollingLinkSlots,
  updateRollingLink,
} from "@/api/client";
import {
  RollingAppointmentView,
  RollingLinkRange,
  RollingLinkNoticeKind,
  RollingLinkUpsertRequest,
  RollingLinkView,
  AvailabilityPatternView,
  PublicRollingLinkSlot,
} from "@/api/types";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { markBuilderReturn } from "../builderReturn";
import { EmbedSnippetPanel } from "../EmbedSnippetPanel";
import { rollingLinkDraftHasContent, clearRollingLinkDraft, loadRollingLinkDraft, saveRollingLinkDraft } from "../draft";
import { DurationField } from "../DurationField";
import { useFeature } from "@/features/profile/useFeature";
import { useProfileOptional } from "@/features/profile/ProfileContext";

import { AdmissionRulesEditor } from "@/features/profile/AdmissionRulesEditor";
import { availabilityPatternLinkFor } from "@/features/profile/patternLinks";

import { FillFromPatternMenu } from "../FillFromPatternMenu";
import { MeetingLocationPicker } from "../MeetingLocationPicker";
import { BuilderSocialNote } from "../BuilderSocialNote";
import { ToolbarPopover } from "../ToolbarPopover";
import { BlockedSlot, BookedSlot } from "../BookingCalendar";
import { fillRun } from "../slotEngine";
import { MinNoticeField } from "./MinNoticeField";
import { RollingAvailabilityCalendar } from "./RollingAvailabilityCalendar";
import { VideoOption, videoOptions } from "@/lib/calendarProviders";

/**
 * The Rolling-rolling link host builder. Reuses the ad-hoc booking builder's product discipline — one
 * canvas-tinted settings column of thin-ruled panels, the shared duration/interval control, the AI
 * rich-text description on the right, the meeting-location picker, fill-from-a-availability-pattern — but with
 * rolling link-specific wording and its own distinct fields (no handle: the link is derived from the title;
 * a rolling interval; relative minimum notice; recurring weekly hours).
 */

const DOTTED_CANVAS: React.CSSProperties = {
  backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1.3px)",
  backgroundSize: "14px 14px",
};

const MIN_SLOT = 5;
const MAX_SLOT = 720; // 12 hours

const DAY_TO_NUM: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/**
 * Copy an availability pattern's weekly availability into rolling link bands.
 *
 * <p>This used to do more. A biweekly link could not express a pattern (a pattern has no Week A or
 * Week B), so every band was materialised into both weeks or the saved link would have offered only
 * Week A. The two-week cadence went in V97, and that workaround went with it — which is most of the
 * reason the cadence went.</p>
 */
function typeToRanges(t: AvailabilityPatternView): RollingLinkRange[] {
  return t.ranges.flatMap((r) =>
    r.days.map((d) => ({
      weekday: DAY_TO_NUM[d.toLowerCase()] ?? 1,
      earliest: r.earliest,
      latest: r.latest,
    })),
  );
}

/** "HH:mm" local band time → minutes from midnight. */
function hhmmToMin(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + (m || 0);
}

/** Minutes from midnight → zero-padded "HH:mm". */
function minToHhmm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** "30 min" / "1 hr" / "1 hr 30 min". */
function minsText(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h && mm) return `${h} hr ${mm} min`;
  if (h) return `${h} hr`;
  return `${mm} min`;
}

/** Title → URL slug: lowercase, alphanumeric words joined by hyphens (no url-encoding surprises). */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** A settings section. `grouped` sits inside the shared canvas container, separated by thin rules. */
function Panel({
  title,
  action,
  grouped,
  className,
  bodyClassName,
  children,
}: {
  title?: string;
  action?: React.ReactNode;
  grouped?: boolean;
  className?: string;
  bodyClassName?: string;
  children?: React.ReactNode;
}) {
  return (
    <section
      className={cn(grouped ? "p-3" : "rounded-xl bg-muted/40 p-4", className)}
      style={grouped ? undefined : DOTTED_CANVAS}
    >
      {(title || action) && (
        <div className="mb-2 flex min-h-[1.5rem] items-center justify-between gap-2">
          {title && <h3 className="text-xs font-bold uppercase tracking-wide text-foreground">{title}</h3>}
          {action}
        </div>
      )}
      {children && <div className={bodyClassName}>{children}</div>}
    </section>
  );
}

/** The "tokens used" pill beside the AI button. */
function TokenBadge({ value }: { value: number }) {
  return (
    <span
      title="Tokens the last AI draft used"
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-contrast/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-brand-contrast"
    >
      <Sparkles className="h-3 w-3" />~{value.toLocaleString()} tokens
    </span>
  );
}

export function RollingLinkBuilder({
  existing,
  onSaved,
  onCancel,
  onError,
}: {
  existing?: RollingLinkView | null;
  onSaved: (rollingLink: RollingLinkView, created: boolean) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  // Per-link branding. Hydrated from the link being edited, so opening an existing one does not
  // quietly switch a host's choice back on; a new link starts with both on.
  const [showHostAvatar, setShowHostAvatar] = React.useState(existing?.show_host_avatar ?? true);
  const [showHostBanner, setShowHostBanner] = React.useState(existing?.show_host_banner ?? true);
  const navigate = useNavigate();

  const timezone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  // Session autosave, same as the ad-hoc builder: a NEW rolling appointment refills itself if you
  // navigate away (or reload) and come back, and shows a "Draft restored" chip. An existing rolling link
  // loads its saved values instead, so it never restores a draft.
  const draft0 = React.useMemo(() => (existing ? null : loadRollingLinkDraft()), [existing]);
  const [restored, setRestored] = React.useState(() => rollingLinkDraftHasContent(draft0));
  const [fading, setFading] = React.useState(false);
  React.useEffect(() => {
    if (!restored) return;
    setFading(false);
    const fadeAt = window.setTimeout(() => setFading(true), 2500);
    const removeAt = window.setTimeout(() => setRestored(false), 5500);
    return () => {
      window.clearTimeout(fadeAt);
      window.clearTimeout(removeAt);
    };
  }, [restored]);

  const [title, setTitle] = React.useState(existing?.title ?? draft0?.title ?? "");
  const [description, setDescription] = React.useState(existing?.description ?? draft0?.description ?? "");

  const [slotMinutes, setSlotMinutes] = React.useState(existing?.slot_minutes ?? draft0?.slotMinutes ?? 30);

  // "Interval" is the GAP between appointments (0 = back-to-back). On the wire the backend's
  // interval_minutes is the stride between starts, so stride = duration + gap.
  const [gapMinutes, setGapMinutes] = React.useState(
    existing && existing.interval_minutes > existing.slot_minutes
      ? existing.interval_minutes - existing.slot_minutes
      : (draft0?.gapMinutes ?? 0),
  );

  // Draw-cursor size (minutes): the block a plain click / hover cursor places when hand-drawing.
  // Independent of the appointment (fill) duration + interval — it only sizes the cursor, not saved.
  const [cursorMinutes, setCursorMinutes] = React.useState(30);

  const [noticeKind, setNoticeKind] = React.useState<RollingLinkNoticeKind>(
    existing?.notice_kind ?? draft0?.noticeKind ?? "MINUTES",
  );
  const [noticeMinutes, setNoticeMinutes] = React.useState(
    existing?.min_notice_minutes ?? draft0?.noticeMinutes ?? 0,
  );

  // No end date is sent. This used to default to a hardcoded 12 months on the grounds that the
  // backend would cap it by tier — but Basic's cap WAS a year, so it passed, and that one date then
  // bounded the free/busy query too. Asking Google for a year at once is rejected as too long, and
  // the busy guard reads a failed lookup as "no conflicts", so every rolling link built here shipped
  // with its double-booking protection silently off. The server derives the end date now.

  const [provider, setProvider] = React.useState(existing?.conferencing_provider ?? draft0?.provider ?? "");
  const [physicalOn, setPhysicalOn] = React.useState(existing ? !!existing.location : (draft0?.physicalOn ?? false));
  const [location, setLocation] = React.useState(existing?.location ?? draft0?.location ?? "");

  // A new rolling link starts with an EMPTY calendar (or a restored draft's hours) — the host draws their
  // own hours; nothing is offered until they do. (An existing rolling link loads its saved availability.)
  const [ranges, setRanges] = React.useState<RollingLinkRange[]>(
    existing?.availability?.length ? existing.availability : (draft0?.ranges ?? []),
  );


  const [previewSlots, setPreviewSlots] = React.useState<PublicRollingLinkSlot[]>([]);
  // Slots the preview dropped for clashing with a meeting — shown as disabled conflict markers.
  const [blockedSlots, setBlockedSlots] = React.useState<BlockedSlot[]>([]);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  // An existing rolling link's confirmed bookings, so the calendar can show its taken slots. The journal
  // is NOT loaded here: an audit log belongs to the read-only view, not next to the fields you're
  // editing. It lives in the History tab of "All bookings".
  const existingId = existing?.id ?? null;
  const [appointments, setAppointments] = React.useState<RollingAppointmentView[]>([]);
  const loadHistory = React.useCallback(() => {
    if (!existingId) {
      setAppointments([]);
      return;
    }
    listRollingAppointments(existingId)
      .then(setAppointments)
      .catch(() => {
        // Secondary — never block editing on it.
      });
  }, [existingId]);
  React.useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Confirmed bookings, projected onto the calendar as taken slots (labelled with the invitee).
  const bookedSlots = React.useMemo<BookedSlot[]>(
    () =>
      appointments
        .filter((a) => a.status === "CONFIRMED")
        .map((a) => ({ start: a.start, end: a.end, label: a.invitee_name ?? a.invitee_email })),
    [appointments],
  );

  const [availabilityPatterns, setAvailabilityPatterns] = React.useState<AvailabilityPatternView[]>([]);
  const marketplace = useFeature("A2A_MARKETPLACE");
  // The account-level half. A link switch on an unreachable account reaches nobody, and saying so
  // here is the difference between a switch that does nothing and one that explains itself (D-89).
  const profile = useProfileOptional()?.state;
  const hostReachable = profile?.status === "ready" && profile.profile.a2aHostEnabled;
  const [filledTypeId, setFilledTypeId] = React.useState(draft0?.filledTypeId ?? "");
  React.useEffect(() => {
    listAvailabilityPatterns()
      .then(setAvailabilityPatterns)
      .catch(() => {
        /* non-fatal — the fill-from-type panel just won't show */
      });
  }, []);

  // Which video options are offerable follows the connected calendar; see the ad-hoc builder.
  const [videoChoices, setVideoChoices] = React.useState<VideoOption[] | null>(null);
  React.useEffect(() => {
    listIntegrations()
      .then((v) => setVideoChoices(videoOptions(v)))
      .catch(() => {
        setVideoChoices(videoOptions(null));
      });
  }, []);

  const [aiEntitled, setAiEntitled] = React.useState(false);
  const [aiDrafting, setAiDrafting] = React.useState(false);
  const [lastDraftTokens, setLastDraftTokens] = React.useState<number | null>(null);
  React.useEffect(() => {
    getEntitlements()
      .then((e) => setAiEntitled(e.tier === "PRO" || e.tier === "ENTERPRISE"))
      .catch(() => {
        /* non-fatal — the server enforces the gate on draft */
      });
  }, []);

  const [saving, setSaving] = React.useState(false);

  // Seed the description textarea to the settings (left) column height so the description canvas +
  // editor line up with the settings and grow together — same imperative approach as the ad-hoc
  // builder (the editor's `fill` is a fixed height, so flex-stretching just leaves an empty canvas).
  const railRef = React.useRef<HTMLDivElement>(null);
  const descColRef = React.useRef<HTMLDivElement>(null);
  const descTaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const descUserResized = React.useRef(false);
  React.useLayoutEffect(() => {
    const rail = railRef.current;
    const col = descColRef.current;
    const ta = descTaRef.current;
    if (!rail || !col || !ta) return;
    const applySeed = () => {
      if (descUserResized.current) return;
      const chrome = col.offsetHeight - ta.offsetHeight; // title, toolbar/footer, paddings
      ta.style.height = `${Math.max(160, Math.round(rail.offsetHeight - chrome))}px`;
    };
    applySeed();
    document.fonts?.ready.then(() => applySeed()).catch(() => {});
    const ro = new ResizeObserver(() => applySeed());
    ro.observe(rail);
    const onResize = () => applySeed();
    window.addEventListener("resize", onResize);
    const onDown = (e: PointerEvent) => {
      const r = ta.getBoundingClientRect();
      if (e.clientX >= r.right - 22 && e.clientY >= r.bottom - 22) descUserResized.current = true;
    };
    ta.addEventListener("pointerdown", onDown);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      ta.removeEventListener("pointerdown", onDown);
    };
  }, []);

  const slug = existing ? existing.handle : slugify(title);

  const rangesValid = ranges.length > 0 && ranges.every((r) => r.earliest < r.latest);
  const canSave = title.trim().length > 0 && !!slug && rangesValid && !saving;

  const createHint = (() => {
    if (!title.trim()) return "Personalise your rolling appointment. Give it a suitable title";
    if (!slug) return "Give the title some letters or numbers so it can make a link.";
    if (!ranges.length) return "Add at least one band of weekly hours.";
    if (!rangesValid) return "Every band needs an end time after its start.";
    return null;
  })();

  const draftWithAi = async (): Promise<void> => {
    if (!aiEntitled) {
      navigate("/upgrade");
      return;
    }
    if (title.trim() === "") {
      onError("Give the rolling appointment a title first. The AI drafts the description from it.");
      return;
    }
    setAiDrafting(true);
    try {
      const draft = await draftBookingDescription({
        title: title.trim(),
        description: description.trim() || undefined,
      });
      setDescription(draft.markdown);
      setLastDraftTokens((draft.input_tokens ?? 0) + (draft.output_tokens ?? 0));
    } catch (e) {
      onError(apiErrorMessage(e, "Couldn't draft a description just now"));
    } finally {
      setAiDrafting(false);
    }
  };

  const buildRequest = React.useCallback(
    (): RollingLinkUpsertRequest => ({
      handle: slug,
      title: title.trim(),
      description: description.trim() || null,
      slot_minutes: slotMinutes,
      timezone,
      min_notice_minutes: noticeKind === "MINUTES" ? Math.max(0, noticeMinutes) : 0,
      notice_kind: noticeKind,
      interval_minutes: slotMinutes + Math.max(0, gapMinutes),
      // Derived server-side from the owner's plan horizon.
      valid_until: null,
      conferencing_provider: provider ? (provider as RollingLinkUpsertRequest["conferencing_provider"]) : null,
      location: physicalOn && location.trim() ? location.trim() : null,
      availability: ranges,
      show_host_avatar: showHostAvatar,
      show_host_banner: showHostBanner,
    }),
    // Every field the request carries belongs here. The old per-link a2a_reachable was missing, so the memoised
    // closure kept handing back the value it captured at mount: the box ticked on screen, the PUT
    // said false, and the reload told the truth. A host reading that concludes the switch is
    // broken rather than that their save was.
    [slug, title, description, slotMinutes, timezone, noticeKind, noticeMinutes,
      gapMinutes, provider, physicalOn, location, ranges, showHostAvatar, showHostBanner],
  );

  // Autosave the in-progress NEW rolling link on every change (session-scoped), so leaving and returning
  // — or a reload — comes back to exactly this state. An existing rolling link is never drafted.
  React.useEffect(() => {
    if (existing) return;
    saveRollingLinkDraft({
      title,
      description,
      slotMinutes,
      gapMinutes,
      noticeKind,
      noticeMinutes,
      provider,
      physicalOn,
      location,
      ranges,
      filledTypeId,
    });
  }, [
    existing,
    title,
    description,
    slotMinutes,
    gapMinutes,
    noticeKind,
    noticeMinutes,
    provider,
    physicalOn,
    location,
    ranges,
    filledTypeId,
  ]);

  // Throw away the restored draft and start clean (the "Discard" on the restore chip).
  const discardDraft = () => {
    clearRollingLinkDraft();
    setRestored(false);
    setTitle("");
    setDescription("");
    setRanges([]);
    setFilledTypeId("");
    setSlotMinutes(30);
    setGapMinutes(0);
    setNoticeKind("MINUTES");
    setNoticeMinutes(0);
    setProvider("");
    setPhysicalOn(false);
    setLocation("");
  };

  // Live preview: whenever the draft changes, ask the server what invitees would be offered
  // (busy already removed). Debounced so typing/drawing doesn't spam it.
  React.useEffect(() => {
    if (ranges.length === 0) {
      setPreviewSlots([]);
      setBlockedSlots([]);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = window.setTimeout(() => {
      previewRollingLinkSlots(buildRequest())
        .then((slots) => {
          if (cancelled) return;
          // Split the preview: open slots are bookable; blocked ones (dropped by a meeting clash)
          // render as disabled conflict markers so the host sees WHY they're gone.
          setPreviewSlots(slots.filter((s) => !s.blocked));
          setBlockedSlots(
            slots
              .filter((s) => s.blocked)
              .map((s) => ({ start: s.start, end: s.end, conflictAt: s.conflict_at })),
          );
        })
        .catch(() => {
          if (!cancelled) {
            setPreviewSlots([]);
            setBlockedSlots([]);
          }
        })
        .finally(() => !cancelled && setPreviewLoading(false));
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [buildRequest, ranges.length]);

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    const req = buildRequest();
    try {
      const saved = existing ? await updateRollingLink(existing.id, req) : await createRollingLink(req);
      if (existing) loadHistory();   // surface the just-logged config changes without a reload
      if (!existing) clearRollingLinkDraft(); // the draft became a real rollingLink — don't offer to restore it
      onSaved(saved, !existing);
    } catch (e) {
      const message = apiErrorMessage(e);
      onError(
        /handle/i.test(message)
          ? "You already have a rolling appointment with a similar title. Tweak the title so its link is unique."
          : message,
      );
    } finally {
      setSaving(false);
    }
  };

  // Merge the selected availability pattern's weekly hours into the drawn availability, keeping any
  // hand-drawn slots (deduped by band). Shared by the toolbar Fill popover.
  const fillFromType = (typeId?: string) => {
    const t = availabilityPatterns.find((x) => x.id === (typeId ?? filledTypeId));
    if (!t || !t.ranges.length) return;
    const gap = Math.max(0, gapMinutes);
    const key = (r: RollingLinkRange) =>
      `${r.weekday}|${r.earliest}|${r.latest}`;
    setRanges((prev) => {
      // Chunk each type band {weekday, earliest, latest} into DURATION-sized bands spaced by the
      // interval gap, placing a band only where it doesn't overlap a hand-drawn band on the SAME
      // weekday. Fixed-grid fillRun wastes colliding positions rather than laying one
      const seen = new Set(prev.map(key));
      const out = [...prev];
      for (const band of typeToRanges(t)) {
        const obstacles = prev
          .filter((r) => r.weekday === band.weekday)
          .map((r) => ({ start: hhmmToMin(r.earliest), end: hhmmToMin(r.latest) }));
        const { blocks } = fillRun(hhmmToMin(band.earliest), hhmmToMin(band.latest), slotMinutes, gap, obstacles);
        for (const b of blocks) {
          const chunk: RollingLinkRange = {
            weekday: band.weekday,
            earliest: minToHhmm(b.start),
            latest: minToHhmm(b.end),
          };
          const k = key(chunk);
          if (seen.has(k)) continue;
          seen.add(k);
          out.push(chunk);
        }
      }
      return out;
    });
  };

  // Settings summary for the calendar toolbar (mirrors the ad-hoc builder): the live config sits on
  // top of the calendar so the host sees what the drawn hours produce. Duration + interval are now
  // editable in place (a flat popover); the rest stay read-only chips that mirror the side panels.
  const chipClass =
    "inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 font-medium text-foreground";
  const locationText = provider ? "Video call" : physicalOn && location.trim() ? location.trim() : null;
  const configSummary = (
    <div className="flex flex-wrap items-center justify-center gap-1.5 text-xs">
      <ToolbarPopover
        align="center"
        active
        title="Size of the slot the draw cursor places"
        ariaLabel="Cursor duration"
        menuClassName="w-72"
        label={
          <>
            <MousePointer2 className="h-3.5 w-3.5 text-brand-blue" /> {minsText(cursorMinutes)}
          </>
        }
      >
        <div className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-wide text-foreground">Cursor duration</span>
          <DurationField minutes={cursorMinutes} onChange={setCursorMinutes} min={MIN_SLOT} max={MAX_SLOT} />
        </div>
      </ToolbarPopover>
      {locationText && (
        <span className={chipClass} title="Meeting location">
          {provider ? (
            <Link2 className="h-3.5 w-3.5 text-brand-blue" />
          ) : (
            <MapPin className="h-3.5 w-3.5 text-amber-600" />
          )}
          {locationText}
        </span>
      )}
    </div>
  );

  // The toolbar Fill/Clear controls (left of the calendar, beside "Starts"), replacing the old side
  // panel. Fill pill shows the chosen type's name (trimmed); a Clear pill appears whenever there are
  // any drawn/filled hours.
  const filledType = availabilityPatterns.find((x) => x.id === filledTypeId);
  const fillPillLabel = filledType
    ? filledType.name.length > 12
      ? `${filledType.name.slice(0, 12)}…`
      : filledType.name
    : "Fill";
  const fillControl = (
    <div className="flex items-center gap-1.5">
      {availabilityPatterns.length > 0 && (
        <ToolbarPopover
          active={!!filledTypeId}
          title="Fill the calendar from an availability pattern"
          ariaLabel="Fill from a pattern"
          label={
            <>
              <Sparkles className="h-3.5 w-3.5 text-brand-blue" /> {fillPillLabel}
            </>
          }
        >
          {(close) => (
            <FillFromPatternMenu
              availabilityPatterns={availabilityPatterns}
              selectedId={filledTypeId}
              duration={slotMinutes}
              onDurationChange={setSlotMinutes}
              interval={gapMinutes}
              onIntervalChange={setGapMinutes}
              onPick={(id) => {
                setFilledTypeId(id);
                fillFromType(id);
              }}
              close={close}
            />
          )}
        </ToolbarPopover>
      )}
      {ranges.length > 0 && (
        <button
          type="button"
          onClick={() => {
            setRanges([]);
            setFilledTypeId(""); // reset the Fill pill to an unset state
          }}
          title="Clear all hours"
          className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition hover:border-red-400 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30"
        >
          <Trash2 className="h-3.5 w-3.5" /> Clear
        </button>
      )}
    </div>
  );

  return (
    <div className="comic-shadow relative space-y-5 rounded-xl border border-border bg-card p-5">
      {restored && (
        <div
          onTransitionEnd={(e) => {
            if (fading && e.propertyName === "opacity") setRestored(false);
          }}
          className={cn(
            // Floating chip in the card's top-right corner — overlaid (absolute), so its arrival and
            // fade-out never reflow the builder beneath it.
            "absolute right-4 top-4 z-20 flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-full border border-brand-blue/40 bg-brand-blue/10 px-3 py-1.5 text-xs shadow-sm backdrop-blur transition-opacity duration-[2500ms] ease-in",
            fading ? "opacity-0" : "opacity-100",
          )}
        >
          <span className="inline-flex items-center gap-1.5 font-medium text-brand-blue">
            <History className="h-3.5 w-3.5 shrink-0" />
            Draft restored. We kept what you were working on.
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={discardDraft}
              className="rounded-full px-2 py-0.5 font-semibold text-brand-blue underline-offset-2 hover:underline"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={() => setRestored(false)}
              title="Dismiss"
              aria-label="Dismiss"
              className="grid h-6 w-6 place-items-center rounded-full text-brand-blue hover:bg-brand-blue/10"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      )}
      {/* Settings column is narrower now that duration/interval/fill moved to the toolbar — give the
          description (AI drafting) the extra width. */}
      <div className="grid gap-4 lg:grid-cols-[2fr_3fr] lg:items-start">
        {/* Left column — settings (its height seeds the description textarea on the right) */}
        <div ref={railRef} className="min-w-0 space-y-4">
          {/* Title + derived link (plain, above the settings canvas) */}
          <div className="space-y-1">
            <Label htmlFor="cl-title" className="text-xs font-bold uppercase tracking-wide text-foreground">
              Title
            </Label>
            <Input
              id="cl-title"
              value={title}
              maxLength={120}
              placeholder="e.g. Weekly office hours"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* All settings on one canvas, separated by thin rules (like the ad-hoc builder). Duration,
              interval and fill-from-type now live as pills on the calendar toolbar, so this column
              carries only notice / link-lifespan / location. */}
          <div className="divide-y divide-border rounded-xl bg-muted/40" style={DOTTED_CANVAS}>
            <Panel grouped title="Minimum notice required before confirming">
              <MinNoticeField
                value={{ kind: noticeKind, minutes: noticeMinutes }}
                onChange={(next) => {
                  setNoticeKind(next.kind);
                  setNoticeMinutes(next.minutes);
                }}
              />
            </Panel>


            {/* Picker sits inline beside the label (Panel action), like the ad-hoc builder; the Meet
                note drops below only when Meet is chosen. */}
            <Panel
              grouped
              title="Meeting location"
              action={
                <div className="min-w-0 max-w-[15rem] flex-1">
                  <MeetingLocationPicker
                    provider={provider}
                    onProviderChange={setProvider}
                    physicalOn={physicalOn}
                    onPhysicalChange={setPhysicalOn}
                    location={location}
                    onLocationChange={setLocation}
                    videoOptions={videoChoices}
                    onConnect={() => {
                      markBuilderReturn("rollingLink");
                      navigate("/profile#calendar-integrations");
                    }}
                  />
                </div>
              }
            >
              {provider && (
                <p className="text-sm leading-snug text-brand-blue">
                  A unique link is created for each appointment and added to its confirmation.
                </p>
              )}
            </Panel>

            <BuilderSocialNote
              offering="rollingLink"
              showAvatar={showHostAvatar}
              showBanner={showHostBanner}
              onShowAvatarChange={setShowHostAvatar}
              onShowBannerChange={setShowHostBanner}
            />
          </div>
        </div>

        {/* Right column — description with AI + rich text. Its textarea is seeded to the settings
            (left column) height so the canvas + editor line up and grow together. */}
        <div ref={descColRef} className="min-w-0">
          <Panel
            title="Description (optional)"
            action={
              <div className="flex items-center gap-2">
                {lastDraftTokens != null && <TokenBadge value={lastDraftTokens} />}
                <button
                  type="button"
                  onClick={draftWithAi}
                  disabled={aiDrafting}
                  title={
                    aiEntitled
                      ? "Let AI draft from your title, or improve the text you've written"
                      : "Drafting is a Pro feature. See plans"
                  }
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-brand-contrast/30 bg-brand-contrast/10 px-2.5 py-1 text-xs font-semibold text-brand-contrast transition hover:bg-brand-contrast/15 disabled:opacity-60"
                >
                  <Sparkles className="h-3 w-3 shrink-0" />
                  {aiDrafting
                    ? "Drafting…"
                    : aiEntitled
                      ? "Redraft with AI from title + description"
                      : "Redraft with AI from title + description (Pro)"}
                </button>
              </div>
            }
          >
            <MarkdownEditor
              id="cl-desc"
              value={description}
              onChange={setDescription}
              maxLength={300}
              placeholder="What's this rolling appointment for? Use the toolbar, or let AI draft a first version."
              taRef={descTaRef}
              fill
            />
          </Panel>
        </div>
      </div>

      {/*
        Who may take this time. The rules live on the pattern this link owns, and are edited here
        because this is where a host thinks about audience: they are deciding who may take THIS
        offer, and the link is the offer (D-90).
      */}
      {marketplace === "granted" && hostReachable && existing?.availability_pattern_id && (
        <AdmissionRulesEditor patternId={existing.availability_pattern_id} />
      )}

      {/*
        Where this link's hours are recorded as a pattern. Shown because a host who meets one of
        these in their pattern list should be able to get here, and because the reverse walk is the
        one that explains it: the pattern is minted from this grid and rewritten whenever it changes
        (D-88).
      */}
      {existing?.availability_pattern_id && (
        <p className="text-[11px] text-muted-foreground">
          These hours are also kept as an availability pattern for this link.{" "}
          {/*
            Deep link to that one pattern, not to the section: a host with a dozen patterns should
            land on the card, which flashes as it arrives (ProfilePage scrolls the hash). A router
            Link, not an <a> — a full page load would drop the autosaved draft and the history
            entry the back crumb on the profile pops to get here again.
          */}
          <RouterLink
            to={availabilityPatternLinkFor(existing.availability_pattern_id)}
            state={{ from: { label: `“${title || "this rolling link"}”` } }}
            className="underline hover:text-foreground"
          >
            See it in your patterns →
          </RouterLink>
        </p>
      )}

      {/* Availability (full width): the weekly drag calendar + live preview. */}
      <RollingAvailabilityCalendar
        ranges={ranges}
        onRangesChange={setRanges}
        slotMinutes={slotMinutes}
        cursorMinutes={cursorMinutes}
        intervalMinutes={slotMinutes + Math.max(0, gapMinutes)}
        previewSlots={previewSlots}
        previewLoading={previewLoading}
        bookedSlots={bookedSlots}
        blockedSlots={blockedSlots}
        configSummary={configSummary}
        fillControl={fillControl}
      />

      <p className="text-xs text-muted-foreground">
        All times in <span className="font-medium text-foreground">{timezone}</span>.
      </p>

      {/* Footer */}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={!canSave} title={createHint ?? undefined}>
          {saving ? "Saving…" : existing ? "Save changes" : "Create link"}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        {createHint && (
          <span className="text-sm font-medium text-amber-700 dark:text-amber-500">{createHint}</span>
        )}
      </div>

      {/*
        Embed, below the Save button on purpose: the host who has just finished building a link is
        exactly the person who wants to put it on their website, and reaching it meant leaving the
        builder for the All links drawer. That copy is kept too — this is where you find it while
        building, that is where you find it again months later.

        Only when editing. A link that does not exist yet has no URL, so there is nothing to embed
        and a snippet pointing at a not-yet-created path would be worse than no snippet at all.

        public_path carries no leading slash (AllLinksView makes the same join), and the snippet
        needs an absolute URL because it is pasted into somebody else's site.
      */}
      {existing && (
        <section>
          <h3 className="mb-2 text-sm font-bold text-foreground">Embed</h3>
          <EmbedSnippetPanel url={`${window.location.origin}/${existing.public_path}`} />
        </section>
      )}

      {/* Journal last: an existing rolling link's config-change + booking history, filterable. */}
    </div>
  );
}
